'use client';

import { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import { Trash2, X } from 'lucide-react';
import { Button } from '@/components/ui/Button/Button';
import LoadingDots from '@/components/ui/LoadingDots/LoadingDots';
import { toast } from '@/components/ui/Toasts/use-toast';
import {
  AccountImpactSummary,
  EmailGateFields,
  EscalationFields,
  PreviewLoading,
  emailGateReady,
  emptyEmailGate,
  useAccountPreview,
  type EmailGateState,
} from './AccountGates';

/**
 * Moderator hard delete. Irreversible: the account, its tones, models,
 * trainings and uploaded files all go, and its username is freed.
 *
 * Every gate here is also enforced in /api/admin/users/delete — this is the
 * flow, not the guard.
 */

interface Props {
  trigger: React.ReactNode;
  userId: string;
  username: string | null;
  /** Where to go once the account no longer exists. Defaults to a refresh,
   * which is wrong on the deleted account's own profile page. */
  redirectTo?: string;
  onSuccess?: () => void;
}

export const DeleteAccountModal = ({
  trigger,
  userId,
  username,
  redirectTo,
  onSuccess,
}: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const router = useRouter();
  const modalRef = useRef<HTMLDivElement>(null);

  const [emailGate, setEmailGate] = useState<EmailGateState>(emptyEmailGate);
  const [acknowledged, setAcknowledged] = useState(false);
  const [typedUsername, setTypedUsername] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { preview, loading, error: previewError } = useAccountPreview(isOpen ? userId : null);

  const reset = () => {
    setEmailGate(emptyEmailGate);
    setAcknowledged(false);
    setTypedUsername('');
    setError(null);
  };

  const toggleModal = () => {
    if (isSubmitting) return;
    if (isOpen) reset();
    setIsOpen(!isOpen);
  };

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        toggleModal();
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleOutsideClick);
    }
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isSubmitting]);

  const escalating = preview?.requiresEscalation ?? false;
  const canSubmit =
    !!preview &&
    !isSubmitting &&
    emailGateReady(emailGate) &&
    (!escalating || (acknowledged && typedUsername.trim() === (preview.username ?? '')));

  const handleSubmit = async () => {
    if (!preview) return;
    setIsSubmitting(true);
    setError(null);
    try {
      const response = await fetch('/api/admin/users/delete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId,
          email: emailGate.skip ? undefined : emailGate.email.trim(),
          skipEmailVerification: emailGate.skip,
          skipReason: emailGate.skip ? emailGate.skipReason.trim() : undefined,
          escalationAck: escalating ? acknowledged : undefined,
          confirmUsername: escalating ? typedUsername.trim() : undefined,
        }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to delete account');
      }

      const incomplete =
        (data.storage?.failures?.length ?? 0) > 0 || (data.storage?.unparseable?.length ?? 0) > 0;

      toast({
        title: `Deleted @${data.username ?? username ?? userId}`,
        description: incomplete
          ? 'Account removed. Some storage objects could not be deleted — see the audit row.'
          : 'Account and all its content removed.',
      });

      reset();
      setIsOpen(false);
      onSuccess?.();
      if (redirectTo) {
        router.push(redirectTo);
      } else {
        router.refresh();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete account');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <button onClick={toggleModal} className="text-zinc-500 cursor-pointer hover:text-white p-1">
        {trigger}
      </button>
      {isOpen && (
        <span className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-40">
          <span
            ref={modalRef}
            className="bg-zinc-900 border border-zinc-500 border-opacity-50 text-white w-full max-w-lg rounded-xl shadow-xl flex flex-col max-h-[90vh] overflow-hidden"
          >
            <span className="flex justify-between items-center p-4 border-b border-zinc-700 flex-shrink-0">
              <span className="text-xl font-semibold flex items-center gap-2">
                <Trash2 className="w-5 h-5 text-red-400" />
                Delete account
              </span>
              <button onClick={toggleModal} className="text-gray-400 hover:text-white">
                <X size={24} />
              </button>
            </span>

            <span className="p-4 flex flex-col gap-4 overflow-y-auto">
              {loading && <PreviewLoading />}
              {previewError && <div className="text-red-400 text-sm">{previewError}</div>}

              {preview && (
                <>
                  <AccountImpactSummary preview={preview} />

                  <span className="text-zinc-400 text-sm">
                    This permanently removes the account, everything it owns and its uploaded
                    files. The username becomes available again. It cannot be undone.
                  </span>

                  <EmailGateFields
                    state={emailGate}
                    onChange={setEmailGate}
                    subject="this account"
                    disabled={isSubmitting}
                  />

                  {escalating && (
                    <EscalationFields
                      preview={preview}
                      typedUsername={typedUsername}
                      onTypedUsernameChange={setTypedUsername}
                      acknowledged={acknowledged}
                      onAcknowledgedChange={setAcknowledged}
                      disabled={isSubmitting}
                      consequence="Deleting it removes that content from every page that links to it."
                    />
                  )}

                  {error && <div className="text-red-400 text-sm">{error}</div>}

                  <span className="flex flex-row gap-4 pt-2">
                    <Button
                      className="w-full"
                      onClick={toggleModal}
                      isSecondary={true}
                      variant="slim"
                      disabled={isSubmitting}
                    >
                      Cancel
                    </Button>
                    <Button
                      className="w-full"
                      onClick={handleSubmit}
                      isWarning={true}
                      variant="slim"
                      disabled={!canSubmit}
                    >
                      {isSubmitting ? <LoadingDots /> : 'Delete permanently'}
                    </Button>
                  </span>
                </>
              )}
            </span>
          </span>
        </span>
      )}
    </>
  );
};

export default DeleteAccountModal;
